import * as _ from "lodash";
import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, Observable, of, Subject, Subscription, throwError} from "rxjs";
import {catchError, map, mergeMap, takeUntil} from "rxjs/operators";
import {ConstantsService} from "../../core/_services/constants.service";
import {JobService} from '../../core/_services/job.service';
import {PartnerServicesService} from '../../core/_services/partner-services.service';
import {UserService} from "../../core/_services/user.service";
import {UtilitiesService} from '../../core/_services/utilities.service';
import {JOB_REQUEST_STATUS, JOB_REQUEST_TYPE, PRODUCT_TYPE, SERVICE_CODE} from "../../core/constants";
import {EcommerceProductsPipe} from "../../shared/_pipes/ecommerce-products.pipe";
import {JobRequestNotes} from '../models/jobRequestNotes';
import {JobRequestTime} from "../models/jobRequestTime";
import {BookingFlowPricingService} from "./booking-flow-pricing.service";
import {AuthService} from "../../core/_services/auth.service";
import {CampaignEventsService} from "./campaign-events.service";
import {GetQuoteParams} from "../../shared/_models/getQuoteParams";
import {Vehicle} from "../../shared/_models/vehicle";
import {Service} from "app/shared/_models/service";
import {PrepopulatedProduct} from "../models/prepopulatedProduct";
import {Partner} from "../../shared/_models/partner";

@Injectable({
  providedIn: 'root'
})

export class JobRequestService implements OnDestroy {

    private jobRequestValue: any = {
        userId: null,
        vehicle: {} as Vehicle,
        services: [],
        location: {},
        times: [],
        paymentMethod: {},
        flexible: false,
        leadTime: 0,
        notes: [] as JobRequestNotes[],
        payerId: 0,
        customerSelectedProviderId: null,
        externalId: null,
        quoteStartDate: null
    };

    /**
     * currently used in new location component as the market of the chosen location could be different than what is
     * stored in UserData, and the new location component does not save the market on the location object
     * 3 digit market code
     * @type {string}
     */
    market: string;

    jobRequest = new BehaviorSubject<any>(this.jobRequestValue);
    quote = new BehaviorSubject<any>({});
    public isCustomQuote: boolean = false;
    approvalRequired: boolean = false;
    userDataSubscription: Subscription;
    referralAffiliate: Partner = {} as Partner;

    private preselectedProductIds: PrepopulatedProduct[] = [];
    public getAvailabilityResponse: any;
    public showPayment: boolean;
    public prePayment: {nonce: string, amount: number, paymentMethodId: number} = null;

    private _onDestroy = new Subject<void>();

    constructor(private bookingFlowPricingService: BookingFlowPricingService,
                public userService: UserService,
                private authService: AuthService,
                private constantsService: ConstantsService,
                private jobService: JobService,
                private partnerService: PartnerServicesService,
                private ecommerceProductsPipe: EcommerceProductsPipe,
                private utilitiesService: UtilitiesService,
                private campaignEventsService: CampaignEventsService) {
        this.userDataSubscription = this.userService.getUserData().subscribe((userData: any) => {
            this.jobRequestValue.userId = UtilitiesService.isNotEmpty(userData) ? userData.id : null;
        });

        this.constantsService.APP_CONSTANTS.pipe(takeUntil(this._onDestroy)).subscribe(() => {
            this.showPayment = this.constantsService.getAppConstants().paymentInTheBookingFlow;
        });
    } // constructor()

    setJobRequest(jr: any) {
        this.jobRequestValue = jr;
    }
    ngOnDestroy(): void {
        this.userDataSubscription.unsubscribe();
    } // ngOnDestroy()

    requiresTime() {
        return this.getServices().findIndex(s => {
            return s.productId === this.constantsService.getProductIdValue().LEMON_SQUAD_USED_CAR_INSPECTION && s.checked;
        }) < 0;
    } // requiresTime()

    hasSelectedServices(): boolean {
        return this.jobRequestValue.services.some((service) => service.checked);
    }

    getJobRequestId(): number {
        return this.jobRequestValue.id;
    } // getJobRequestId()

    setJobRequestId(id: number): void {
        this.jobRequestValue.id = id;
    } // setJobRequestId()

    getUserId(): number {
        return this.jobRequestValue.userId;
    } // getUserId()

    setUserId(userId: number): void {
        this.jobRequestValue.userId = userId;
    } // setUserId()

    getPayerId(): number {
        return this.jobRequestValue.payerId;
    } // getPayerId()

    setPayerId(value: number) {
        this.jobRequestValue.payerId = value;
    } // setPayerId();

    getPreselectedProductIds(): PrepopulatedProduct[] {
        return this.preselectedProductIds;
    } // getPreselectedProductIds()

    setPreselectedProductIds(value: PrepopulatedProduct[]) {
        this.preselectedProductIds = value;
    } // setPreselectedProductIds();

    hasService(serviceCode: number) {
        return this.getServices().findIndex(s => {
            return s.serviceCode === serviceCode;
        }) >= 0;
    } // hasService()

    hasServiceByProductIs(productId: number): boolean {
        return this.getServices().findIndex(s => {
            return s.productId === productId;
        }) >= 0;
    } // hasServiceByProductIs()

    hasSameService(mainService: Service): boolean {
        let serviceFound: boolean = false;
        this.getServices().forEach((service: Service) => {
           if (JSON.stringify(service.extraParams) === JSON.stringify(mainService.extraParams) && JSON.stringify(service.parts) === JSON.stringify(mainService.parts) && mainService.serviceCode === service.serviceCode) {
               serviceFound = true;
           }
        });
        return serviceFound;
    } // hasSameService
    getService(serviceCode: number) {
        const service = this.getServices().find(s => {
            return s.serviceCode === serviceCode;
        });
        return UtilitiesService.isNotEmpty(service) ? UtilitiesService.deepCopy(service) : null;
    } // getService()

    addService(service: any): Observable<any> {
        // if this is a maintenance service, remove any other maintenance services from jobRequestValue.services
        if (service.bundle && service.productType === PRODUCT_TYPE.MAINTENANCE) {
            _.remove(this.jobRequestValue.services, {"bundle": true, "productType": PRODUCT_TYPE.MAINTENANCE});
        }
        // newly added services are always checked
        service = UtilitiesService.deepCopy(service);
        service.checked = true;
        service.hasParts = service.customerSuppliedParts;
        this.jobRequestValue.services.push(service);
        this.jobRequest.next(this.jobRequestValue);
        return this.refreshPrice();
    } // addService()

    addServices(services: Service[]): Observable<any> {
        services.forEach((service: Service) => {
            service.checked = true;
            service.hasParts = service.customerSuppliedParts;
            this.jobRequestValue.services.push(service);
        });
        this.jobRequest.next(this.jobRequestValue);
        return this.refreshPrice();
    } // addServices()

    removeServicesViaProductIds(productIds: number[]): void {
        for (let productId of productIds) {
            let existingService = this.jobRequestValue.services.find((service: any) => service.productId === productId);

            if (UtilitiesService.isNotEmpty(existingService) && UtilitiesService.isNotEmpty(existingService.serviceCode)) {
                this.jobRequestValue.services = this.jobRequestValue.services.filter((service: any) => service.productId != productId);
                this.removeNotes(existingService.serviceCode);
                this.jobRequest.next(this.jobRequestValue);
                this.refreshPrice().subscribe();
            }
        }
    } // removeServicesViaProductIds()

    removeService(serviceToRemove: any): Observable<any> {
        // filter by id if it exists, otherwise filter by service code
        this.jobRequestValue.services = this.jobRequestValue.services.filter((service: any) => {
            return UtilitiesService.isNotEmpty(service.id) ? service.id != serviceToRemove.id : service.serviceCode != serviceToRemove.serviceCode;
        });
        this.removeNotes(serviceToRemove.serviceCode);
        this.jobRequest.next(this.jobRequestValue);
        return this.refreshPrice();
    } // removeService()

    setServiceChecked(serviceToUpdate: any, checkedValue: boolean): Observable<any> {
        const index = this.jobRequestValue.services.findIndex((s: any) => serviceToUpdate.serviceCode === s.serviceCode);
        if (index >= 0) {
            this.jobRequestValue.services[index].checked = checkedValue;
            this.jobRequest.next(this.jobRequestValue);
            return this.refreshPrice();
        }
        return of(false);
    } // setServiceChecked()

    setSimilarServiceChecked(serviceToUpdate: Service, checkedValue: boolean): Observable<any> {
        const index = this.jobRequestValue.services.findIndex((s: Service) =>
            JSON.stringify(s.extraParams) === JSON.stringify(serviceToUpdate.extraParams) && JSON.stringify(s.parts) === JSON.stringify(serviceToUpdate.parts)
            && serviceToUpdate.serviceCode === s.serviceCode
        );
        if (index >= 0) {
            this.jobRequestValue.services[index].checked = checkedValue;
            this.jobRequest.next(this.jobRequestValue);
            return this.refreshPrice();
        }
        return of(false);
    } // setSimilarServiceChecked()

    setAllSimilarServiceChecked(serviceToUpdate: Service, checkedValue: boolean): Observable<boolean> {
        const indices = this.jobRequestValue.services.reduce((acc: number[], s: Service, currentIndex: number) => {
            if (serviceToUpdate.productId === s.productId) {
                acc.push(currentIndex);
            }
            return acc;
        }, []);
        if (indices.length > 0) {
            for (const index of indices) {
                this.jobRequestValue.services[index].checked = checkedValue;
            }
            this.jobRequest.next(this.jobRequestValue);
            return this.refreshPrice();
        }
        return of(false);
    }

    setServiceNotes(serviceToUpdate: any, notes: string) {
        const index = this.jobRequestValue.services.findIndex((s: any) => serviceToUpdate.serviceCode === s.serviceCode);
        if (index >= 0) {
            this.jobRequestValue.services[index].notes = notes;
            this.jobRequest.next(this.jobRequestValue);
        }
    } // setServiceNotes()

    resetServices(): void {
        this.jobRequestValue.services = [];
        this.jobRequest.next(this.jobRequestValue);
        this.quote.next({});
        this.isCustomQuote = false;
        this.approvalRequired = false;
    } // resetServices()

    getServices(): Service[] {
        return this.jobRequestValue.services;
    } // getServices()

    getCheckedServices(): any[] {
        return this.getServices().filter(s => s.checked);
    } // getCheckedServices()

    getCheckedServiceCodes(): number[] {
        return this.getCheckedServices().map(s => s.serviceCode);
    } //  getCheckedServiceCodes()

    getServiceHours(): number {
        return this.quote.value.serviceHours;
    }

    getQuoteObject(): any {
        return this.quote.value;
    }

    addNotes(serviceCode: number, notes: string): void {
        const note = this.jobRequestValue.notes.find((n: JobRequestNotes) => n.serviceCode === serviceCode);
        if (note) {
            note.text = notes;
        } else {
            this.jobRequestValue.notes.push({serviceCode: serviceCode, text: notes});
        }
        this.jobRequest.next(this.jobRequestValue);
    } // setNotes()

    removeNotes(serviceCode: number) {
        this.jobRequestValue.notes.splice(
            this.jobRequestValue.notes.indexOf((n: JobRequestNotes) => n.serviceCode === serviceCode), 1
        );
        this.jobRequest.next(this.jobRequestValue);
    }

    getNotes() {
        let serviceCodes = this.jobRequestValue.services.map(
            (s: any) => {
                if (s.checked) {
                    return s.serviceCode;
                }
            });
        return this.jobRequestValue.notes.reduce(
            (jrNotes: string, val: JobRequestNotes) => {
                return serviceCodes.includes(val.serviceCode) && val?.text ? jrNotes + `${val.text}\n\n` : jrNotes;
            }, '');
    }

    setVehicle(vehicle: Vehicle): void {
        this.jobRequestValue.vehicle = _.clone(vehicle) as Vehicle;
        this.jobRequest.next(this.jobRequestValue);
    } // setVehicle()

    resetVehicle(): void {
        this.jobRequestValue.vehicle = {} as Vehicle;
        this.jobRequest.next(this.jobRequestValue);
    } // resetVehicle()

    getVehicle(): any {
        return this.jobRequestValue.vehicle;
    } // getVehicle()

    setLocation(location: any): void {
        this.jobRequestValue.location = _.clone(location);
        this.jobRequest.next(this.jobRequestValue);
    } // setLocation()

    getLocation(): any {
        return this.jobRequestValue.location;
    } // getLocation()

    setPaymentMethod(paymentMethod: any): void {
        this.jobRequestValue.paymentMethod = _.clone(paymentMethod);
        this.jobRequest.next(this.jobRequestValue);
    } // setPaymentMethod()

    getPaymentMethod(): any {
        return this.jobRequestValue.paymentMethod;
    } // getPaymentMethod()

    setTime(times: JobRequestTime[]) {
        this.getAvailabilityResponse = {};
        this.jobRequestValue.times = times;
        this.jobRequest.next(this.jobRequestValue);
    } // setTime()

    setTimesLocationId(locationId: number) {
        this.jobRequestValue.times[0].locationId = locationId;
        this.jobRequest.next(this.jobRequestValue);
    } // setTimesLocationId()

    setPreferredTechnician(technicianId: number) {
        this.jobRequestValue.customerSelectedProviderId = technicianId ? technicianId : null;
        this.jobRequest.next(this.jobRequestValue);
    }

    setSurchargeDateAndTime(times: JobRequestTime[]): void {
        this.jobRequestValue.quoteStartDate = times[0]?.startDate;
    }

    setCustomerPONumber(PONumber: string) {
        this.jobRequestValue.externalId = PONumber || null;
        this.jobRequest.next(this.jobRequestValue);
    }

    getPreferredTechnician(): number {
        return this.jobRequestValue.customerSelectedProviderId;
    }

    getTime(): JobRequestTime[] {
        return this.jobRequestValue.times;
    } // getTime()

    isFlexible(): boolean {
        return this.jobRequestValue.flexible;
    } // isFlexible()

    getLeadTime(): number {
        return this.jobRequestValue.leadTime;
    } // getLeadTime()

    setLeadTime(leadTime: number): void {
        this.jobRequestValue.leadTime = leadTime;
    } // setLeadTime()

    setFlexible(flexible: boolean) {
        this.jobRequestValue.flexible = flexible;
        this.jobRequest.next(this.jobRequestValue);
    } // setFlexible()

    refreshPrice(): Observable<any> {
        this.quote.next({updating: true});
        const tempServices = this.getCheckedServices();
        if (UtilitiesService.isNotEmpty(tempServices)) {
            const tempJobRequest = UtilitiesService.deepCopy(this.jobRequestValue);
            // We used to flatten the service list.  Now we just pass the parent services and the backend handles the bundles
            tempJobRequest.services = tempServices;
            return this.bookingFlowPricingService.getPrice(tempJobRequest).pipe(
                map((response: any) => {
                    this.checkCustomQuote(response);

                    for (let service of this.jobRequestValue.services) {
                        const serviceCode = service.serviceCode;
                        const discountCode = service.productDiscountCode;
                        const extraParams = service?.extraParams;
                        service.price = response.servicePrices.find((p: any) => {
                            if (Object.keys(extraParams ?? {}).length > 0) {
                                return p?.serviceCode === serviceCode && p?.parts[0]?.aspectRatio === extraParams?.aspectRatio
                                    && p?.parts[0]?.width === extraParams?.width && p?.parts[0]?.rimDiameter === extraParams?.rimDiameter
                                    && p?.parts[0]?.quantity === service?.parts[0]?.quantity;
                            } else {
                                return p.serviceCode === serviceCode;
                            }
                        });
                        service.hasActiveDiscount = false;
                        service.discountAmount = 0;
                        service.txnTotal = 0;

                        if (service.price) {
                            service.txnTotal = service.price.total;
                        }

                        if (response.discounts && discountCode) {
                            const activeDiscount = response.discounts.find((d: any) => d.code === discountCode);
                            if (activeDiscount) {
                                service.discountAmount = activeDiscount.amount;
                                service.hasActiveDiscount = true;
                            }
                        }
                    }

                    this.approvalRequired = response.approvalRequired;
                    this.quote.next(response);
                }),
                catchError((error: any) => {
                    UtilitiesService.consoleLog(error.message);
                    // on error, we just mark the quote as a custom quote
                    this.isCustomQuote = true;
                    this.quote.next({});
                    return of({});
                })
            );
        } else {
            // job request has no services therefore custom quote should be false
            this.isCustomQuote = false;
            this.quote.next({});
            return of({});
        }
    } // refreshPrice()

    getQuote(): Observable<any> {
        if (UtilitiesService.isNotEmpty(this.jobRequestValue.id)) {
            this.quote.next({updating: true});
            const params = {jobRequestId: this.jobRequestValue.id} as GetQuoteParams;
            return this.jobService.getQuote(params).pipe(
                map((response: any) => {
                    this.quote.next(response);
                }),
                catchError((error: any) => {
                    UtilitiesService.consoleLog(error.message);
                    // on error, we just mark the quote as a custom quote
                    this.isCustomQuote = true;
                    this.quote.next({});
                    return of({});
                })
            );
        } else {
            return of({});
        }
    } // getQuote()

    getDiscounts(): any[] {
        const quote = this.quote.getValue();
        return UtilitiesService.isNotEmpty(quote) && UtilitiesService.isNotEmpty(quote.discounts) ? quote.discounts : [];
    }

    reset(): void {
        this.jobRequestValue = {
            vehicle: {},
            services: [],
            location: {},
            times: [],
            paymentMethod: {},
            flexible: false,
            notes: [],
        };
        this.jobRequest.next(this.jobRequestValue);
        if (!this.approvalRequired) {
            this.quote.next({});
        }
        this.isCustomQuote = false;
        this.approvalRequired = false;
    } // reset()

    isValid(): boolean {
        const services = this.getCheckedServices().filter((s: any) => !s.addon);
        // requirements are different for custom quotes(pricing requests)/ quotes that need approval and full job requests
        if (this.isCustomQuote || this.approvalRequired) {
            return this.jobRequestValue.userId > 0 &&
                UtilitiesService.isNotEmpty(this.jobRequestValue.vehicle) &&
                UtilitiesService.isNotEmpty(this.jobRequestValue.vehicle.id) &&
                UtilitiesService.isNotEmpty(services);
        } else {
            const timeValid = this.requiresTime() ? UtilitiesService.isNotEmpty(this.jobRequestValue.times) &&
                UtilitiesService.isNotEmpty(this.jobRequestValue.times[0].startDate) &&
                UtilitiesService.isNotEmpty(this.jobRequestValue.times[0].locationId) : true;
            return this.jobRequestValue.userId > 0 &&
                UtilitiesService.isNotEmpty(this.jobRequestValue.vehicle) &&
                UtilitiesService.isNotEmpty(this.jobRequestValue.vehicle.id) &&
                UtilitiesService.isNotEmpty(services) &&
                timeValid &&
                (this.showPayment ? UtilitiesService.isNotEmpty(this.jobRequestValue.paymentMethod?.id) : true);
        }
    } // isValid()

    createJobRequest(): Observable<any> {
        if (!this.isValid()) {
            return throwError({message: "missing information"});
        }
        const externalReferralId = isNaN(Number(this.utilitiesService.getLocal("externalReferralId"))) ? null : Number(this.utilitiesService.getLocal("externalReferralId"));
        const referrerId = isNaN(Number(this.utilitiesService.getLocal("referrerId"))) ? null : Number(this.utilitiesService.getLocal("referrerId"));
        const referrerType = isNaN(Number(this.utilitiesService.getLocal("referrerType"))) ? null : Number(this.utilitiesService.getLocal("referrerType"));

        // We used to flatten the service list.  Now we just pass the parent services and the backend handles the bundles
        const services = this.getCheckedServices();
        // lemon squad doesn't have job request times, so we must add the locationId to the jobRequest
        const isLemonSquad = this.partnerService.thirdPartyUsedCarInspectionPresent(services);
        const jobRequest = {
            id: this.jobRequestValue.id ? this.jobRequestValue.id : null,
            userId: this.jobRequestValue.userId,
            type: this.isCustomQuote ? JOB_REQUEST_TYPE.ONLINE : JOB_REQUEST_TYPE.INSTANT,
            status: this.getJobRequestStatus(),
            flexible: this.jobRequestValue.flexible,
            vehicleId: this.jobRequestValue.vehicle.id,
            mileage: this.jobRequestValue.vehicle.mileage != null ? this.jobRequestValue.vehicle.mileage : 0,
            locationId: isLemonSquad ? this.jobRequestValue.location.id : 0,
            paymentMethodId: this.jobRequestValue.paymentMethod.preferred ? null : this.jobRequestValue.paymentMethod.id,
            externalReferralId: externalReferralId,
            referrerId: referrerId,
            referrerType: referrerType,
            services: services,
            times: !this.isCustomQuote ? this.jobRequestValue.times : null,
            notes: this.getNotes(),
            locationNotes: this.jobRequestValue.location.notes,
            locationKeyDetails: this.jobRequestValue.location.keyDetails,
            payerId: this.jobRequestValue.payerId,
            partnerId: UtilitiesService.isNotEmpty(this.userService.getCurrentUserData().partnerId)
                ? this.userService.getCurrentUserData().partnerId : null,
            customerSelectedProviderId: this.jobRequestValue.customerSelectedProviderId,
            externalId: this.jobRequestValue.externalId,
            prePayment: this.prePayment,
        };

        return this.userService.createJobRequest(jobRequest).pipe(
            mergeMap((response: any) => {
                if (response.responseCode == 100) {
                    this.setJobRequestId(response.jobRequestId);
                    this.campaignEventsService.logBookedJob(response.jobRequestId);
                    this.campaignEventsService.partnerId = null;
                    this.campaignEventsService.landingPageDetailId = null;

                    // Reset referrer after job request
                    this.utilitiesService.setLocal("referrerId", null);
                    this.utilitiesService.setLocal("referrerType", null);
                    this.utilitiesService.setLocal("externalReferralId", null);

                    return of(response);
                } else {
                    return throwError(response);
                }
            })
        );
    } // createJobRequest()

    // assign jobRequests to partner
    assignPartnerJob(jobRequestId: number): Observable<any> {
        return this.jobService.assignPartnerJob({id: jobRequestId}, this.constructor.name);
    } // assignPartnerJob()

    private checkCustomQuote(quote: any): void {
        const services: any = this.getCheckedServices();
        this.isCustomQuote = UtilitiesService.isEmpty(quote)
            || UtilitiesService.isEmpty(services)
            || services.some((s: any) => s.serviceCode === SERVICE_CODE.CUSTOM);
    } // checkCustomQuote()

    private getJobRequestStatus(): any {
        if (this.approvalRequired) {
            return JOB_REQUEST_STATUS.PENDING_APPROVAL;
        } else if (this.isCustomQuote || !this.requiresTime()) {
            return JOB_REQUEST_STATUS.PRICING;
        } else {
            return JOB_REQUEST_STATUS.READY;
        }
    } // getJobRequestStatus()

    getQuoteValue() {
        return this.quote.getValue();
    } // getQuoteValue()

    getAddServiceEcommerce(service: any) {
        const products = this.ecommerceProductsPipe.transform([service]);
        return {
            'add': {'products': products}
        };
    } // getAddServiceEcommerce()

    getPurchaseEcommerce() {
        const products = this.ecommerceProductsPipe.transform(this.jobRequestValue.services);
        const quote = this.quote.getValue();
        let ecommerce: any = {
            purchase: {
                actionField: {
                    id: this.getJobRequestId(),
                    revenue: quote.total,
                    tax: quote.tax
                },
                products: products
            }
        };
        if (UtilitiesService.isNotEmpty(quote.discounts)) {
            ecommerce.purchase.actionField.coupon = quote.discounts[0].code;
        }
        return ecommerce;
    } // getPurchaseEcommerce()

    getSelectedNotesFromServiceListEntry(serviceListEntryOptions: any): string {
        for (let option of serviceListEntryOptions) {
            for (let service of this.jobRequestValue.services) {
                if (option.productId === service.productId) {
                    return service.notes;
                }
            }
        }
        return null;
    } // getSelectedNotesFromServiceListEntry()

    /** gets the availability based on the current state of the job request **/
    getAvailability(): Observable<any> {
        const userData = this.userService.getCurrentUserData();
        let params: any = {};

        if (this.jobRequestValue.location && this.jobRequestValue.location.id) {
            params.locationId = this.jobRequestValue.location.id;
        } else if (this.jobRequestValue.location && UtilitiesService.isNotEmpty(this.jobRequestValue.location.address)) {
            params.address = this.jobRequestValue.location;
        } else if (this.jobRequestValue.location && this.jobRequestValue.location.zip) {
            params.zip = this.jobRequestValue.location.zip;
        } else if (userData && userData.zip) {
            params.zip = userData.zip;
        }

        if (this.jobRequestValue?.customerSelectedProviderId) {
            params.providerId = this.jobRequestValue.customerSelectedProviderId;
        }

        const market = this.getMarket(userData);
        if (UtilitiesService.isNotEmpty(market)) {
            params.market = market;
        }

        const bookingHours = this.getQuoteValue().bookingHours;
        // todo we need to handle when this.getQuoteValue() is "{updating: true}"
        if (UtilitiesService.isNotEmpty(this.jobRequestValue.times) && this.jobRequestValue.times[0].startDate && bookingHours) {
            params.start = this.jobRequestValue.times[0].startDate;
            const startDate = UtilitiesService.dateFromString(this.jobRequestValue.times[0].startDate);
            const endTimestamp = startDate.getTime() + 3600000 * this.getQuoteValue().bookingHours;
            params.end = UtilitiesService.dateFromTimestamp(endTimestamp);
        }

        if (bookingHours) {
            params.bookingHours = bookingHours;
        }

        params.leadTime = this.getLeadTime();
        params.services = this.getCheckedServiceCodes();

        params.userPartnerId = UtilitiesService.isNotEmpty(userData.partnerId)
            ? this.userService.getCurrentUserData().partnerId : null;

        if (this.authService.loggedIn()) {
            return this.jobService.getAvailabilityAuthed(params).pipe(map((result: any) => {
                this.getAvailabilityResponse = result;
                return result;
            }));
        } else {
            return this.jobService.getAvailability(params).pipe(map((result: any) => {
                this.getAvailabilityResponse = result;
                return result;
            }));
        }
    } // getAvailability()

    rescheduleRequired(): boolean {
        const bookingHours = this.getQuoteValue().bookingHours;
        const jobRequest = this.jobRequest.getValue();
        if (bookingHours && UtilitiesService.isNotEmpty(jobRequest.times)) {
            const {startDate, endDate} = jobRequest.times[0];
            const currentBookingHours = (new Date(endDate).getTime() - new Date(startDate).getTime()) / 3600000;
            return currentBookingHours !== bookingHours;
        } else {
            return false;
        }
    } // rescheduleRequired()

    /**
     * @param {string}  userData - current user data stored within user service
     * @returns {string} 3 digit market code associated with the jobRequest
     */
    getMarket(userData: {market: string}): string {
        return this.jobRequest.getValue()?.location?.market || this.market || userData?.market || '';
    } // getMarket()

    /**
     * update a service object on the jobRequest
     */
    updateService(service: Service): void {
        const index = this.jobRequestValue.services.findIndex((s: Service) => service.serviceCode === s.serviceCode);
        if (index >= 0) {
            this.jobRequestValue.services[index] = service;
            this.jobRequest.next(this.jobRequestValue);
        }
    }

    removeExtraServiceDetails(service: Service): void {
        const index = this.jobRequestValue.services.findIndex((s: Service) => service.serviceCode === s.serviceCode);
        if (index >= 0) {
            service.extraParams = null;
            service.parts = null;
            this.jobRequestValue.services[index] = service;
            this.jobRequest.next(this.jobRequestValue);
        }
    }
}
